import { ApolloClient } from '@apollo/client';
import { call, select } from 'redux-saga/effects';

import { inMemoryStorage } from '@app/services/storageManager';
import { selectLocale, selectUser, TokenInfo } from '@app/redux';
import { reduxGraphQL } from '@app/redux/graphql';

import { Locale, TokenType } from '@mfe/shared/schema-types';

import { getLink } from './getLink';
import { createCache } from '../../../../shared/utils/cacheFactory';

export const getClient = (tokenInfo: TokenInfo, locale: Locale, useStorage = true) => {
  const backEndLink = getBackEndLink();
  const isRegistrationToken = tokenInfo.type === TokenType.Registration;
  const token = isRegistrationToken
    ? {
        accessToken: '',
        accessTokenExpirationTime: '',
        locale: Locale.EnUs,
      }
    : tokenInfo;

  const KEY = backEndLink;

  let client = useStorage ? inMemoryStorage.get(KEY) : null;

  if (!client) {
    client = new ApolloClient({
      link: getLink(backEndLink, token, locale),
      cache: createCache(),
    });
    if (useStorage && token?.accessToken) {
      inMemoryStorage.set(KEY, client);
    }
  } else {
    // The client is cached to preserve its in-memory cache, but the link closes
    // over the access token. After a token refresh, re-point it at a link built
    // from the current token so requests use the new token, not the stale one.
    client.setLink(getLink(backEndLink, token, locale));
  }

  return client;
};

function getBackEndLink() {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:3100/api/graphql';
  }

  return `${window.location.origin}/api/graphql`;
}

function* initReduxClient(): any {
  const {
    user: {
      auth: { tokenInfo },
    },
  }: ReturnType<typeof selectUser> = yield select(selectUser);

  const {
    locale: { userLocale },
  }: ReturnType<typeof selectLocale> = yield select(selectLocale);

  const client = yield call(getClient, tokenInfo, userLocale);

  return client;
}

reduxGraphQL.initialize(initReduxClient);
